/**
 * Copy-to-clipboard with feedback that does not rely on colour.
 *
 * The label flips to "Copied" for two seconds, because a copy button that gives
 * no acknowledgement gets pressed three times and trusted zero. Failure says
 * so too — clipboard access can be denied — rather than pretending.
 */

import { useEffect, useRef, useState } from 'react';

import { Button, type ButtonKind, type ButtonSize } from './Button';
import { CopyIcon } from './Icon';

export function CopyButton({
  text,
  label = 'Copy',
  kind = 'ghost',
  size = 'sm',
  disabled = false,
}: {
  /** The text to copy, or a producer for buffers that are expensive to join. */
  text: string | (() => string);
  label?: string;
  kind?: ButtonKind;
  size?: ButtonSize;
  disabled?: boolean;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  async function copy() {
    const value = typeof text === 'function' ? text() : text;
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 2000);
  }

  return (
    <Button
      kind={kind}
      size={size}
      renderIcon={CopyIcon}
      disabled={disabled}
      onClick={copy}
      aria-live="polite"
    >
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : label}
    </Button>
  );
}
